import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { automationRuleService } from '../services/automationRuleService'
import { AutomationRule, AutomationRuleFormData } from '../types/automationRule'
import { Modal } from '../components/Modal'
import { AutomationRuleForm } from '../components/AutomationRuleForm'
import './SettingsTaskAutomation.css'

export function SettingsTaskAutomation() {
  const { hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null)

  // Check if user has permission
  if (!hasPermission('tasks:write') && !hasPermission('*')) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Access Denied</h2>
          <p>You don't have permission to manage automation rules.</p>
        </div>
      </div>
    )
  }

  const { data: rules, isLoading, error } = useQuery({
    queryKey: ['automation-rules'],
    queryFn: () => automationRuleService.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: AutomationRuleFormData) => automationRuleService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] })
      setShowAddForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AutomationRuleFormData> }) =>
      automationRuleService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] })
      setEditingRule(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => automationRuleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      automationRuleService.toggle(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-rules'] })
    },
  })

  const handleSubmit = async (data: AutomationRuleFormData) => {
    if (editingRule) {
      await updateMutation.mutateAsync({ id: editingRule.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete rule "${name}"? This action cannot be undone.`)) {
      try {
        await deleteMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to delete rule')
      }
    }
  }

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      await toggleMutation.mutateAsync({ id, enabled })
    } catch (error) {
      alert('Failed to toggle rule')
    }
  }

  if (showAddForm || editingRule) {
    return (
      <div className="settings-content" role="main">
        {(createMutation.isError || updateMutation.isError) && (
          <div className="error" role="alert">
            Failed to save rule. Please try again.
          </div>
        )}
        <Modal
          isOpen={true}
          onClose={() => {
            setShowAddForm(false)
            setEditingRule(null)
          }}
          title={editingRule ? 'Edit Automation Rule' : 'Create Automation Rule'}
          size="large"
        >
          <AutomationRuleForm
            initialData={editingRule ? { ...editingRule } : undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowAddForm(false)
              setEditingRule(null)
            }}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </Modal>
      </div>
    )
  }

  return (
    <div className="settings-content" role="main">
      <div className="settings-header">
        <div>
          <h1>Task Automation Rules</h1>
          <p className="settings-subtitle">Manage rules for automatic task creation</p>
        </div>
        {hasPermission('tasks:write') && (
          <button
            className="btn-primary"
            onClick={() => setShowAddForm(true)}
            aria-label="Add new automation rule"
          >
            + Add Rule
          </button>
        )}
      </div>

      {isLoading && (
        <div className="loading" role="status" aria-live="polite">
          Loading automation rules...
        </div>
      )}

      {error && (
        <div className="error" role="alert" aria-live="assertive">
          Error loading automation rules
        </div>
      )}

      {!isLoading && !error && rules && (
        <div className="automation-rules-list">
          {rules.length === 0 ? (
            <div className="empty-state">
              <p>No automation rules configured.</p>
              <button className="btn-primary" onClick={() => setShowAddForm(true)}>
                Create First Rule
              </button>
            </div>
          ) : (
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Trigger</th>
                  <th>Condition</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <div className="rule-name">{rule.name}</div>
                      {rule.description && (
                        <div className="rule-description">{rule.description}</div>
                      )}
                    </td>
                    <td>
                      <span className="trigger-badge">{rule.triggerType}</span>
                    </td>
                    <td>
                      <div className="condition-display">
                        {rule.condition.field} {rule.condition.operator} {String(rule.condition.value)}
                      </div>
                    </td>
                    <td>
                      <div className="action-display">
                        <div className="action-title">{rule.action.title}</div>
                        <div className="action-priority">
                          Priority: {rule.action.priority}
                          {rule.action.urgency && ` | Urgency: ${rule.action.urgency}`}
                        </div>
                      </div>
                    </td>
                    <td>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={(e) => handleToggle(rule.id, e.target.checked)}
                          disabled={toggleMutation.isPending}
                        />
                        <span className="toggle-slider">
                          {rule.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </td>
                    <td>
                      <div className="rule-actions">
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => setEditingRule(rule)}
                          aria-label={`Edit rule ${rule.name}`}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-danger btn-sm"
                          onClick={() => handleDelete(rule.id, rule.name)}
                          aria-label={`Delete rule ${rule.name}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="settings-info">
        <h2>Automation Rules</h2>
        <div className="info-sections">
          <div className="info-section">
            <h3>How It Works</h3>
            <ul>
              <li>Rules are evaluated when trigger events occur (e.g., lab result created)</li>
              <li>If the condition matches, a task is automatically created</li>
              <li>Tasks created by automation are marked with <code>isAutomated: true</code></li>
              <li>You can enable/disable rules without deleting them</li>
            </ul>
          </div>
          <div className="info-section">
            <h3>Trigger Types</h3>
            <ul>
              <li><strong>lab-result-created:</strong> When a lab result is created</li>
              <li><strong>lab-result-updated:</strong> When a lab result is updated</li>
              <li><strong>medication-review-due:</strong> When medication review date is reached</li>
              <li><strong>appointment-scheduled:</strong> When an appointment is scheduled</li>
              <li><strong>consultation-completed:</strong> When a consultation is completed</li>
              <li><strong>vital-sign-abnormal:</strong> When a vital sign is outside normal range</li>
            </ul>
          </div>
          <div className="info-section">
            <h3>Condition Operators</h3>
            <ul>
              <li><strong>equals:</strong> Exact match</li>
              <li><strong>notEquals:</strong> Not equal</li>
              <li><strong>contains:</strong> String contains value</li>
              <li><strong>lessThanOrEqual:</strong> Date/number comparison (supports 'now')</li>
              <li><strong>greaterThanOrEqual:</strong> Date/number comparison (supports 'now')</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

